import React from 'react';
import { Button, TextField, Link, Typography, Container, Card, CardContent, InputAdornment, Box } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logOut, setMustSetPassword } from '../redux/slices/auth';
import { updateUserPassword } from '../redux/slices/users';
import styled from '@emotion/styled';
import { useBrandName } from '../utils/brandUtils';

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  minHeight: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.up('sm')]: {
    width: '475px',
    margin: '0 auto',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
}));

const StyledHelper = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const StyledFooterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const MIN_LENGTH = 8;

function SetPassword() {
  const dispatch = useDispatch();
  const brandName = useBrandName();
  const userId = useSelector((state) => state.auth.userId);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [isSaving, setIsSaving] = React.useState(false);

  const validate = () => {
    const next = {};
    if (password.trim().length < MIN_LENGTH) {
      next.password = `Password must be at least ${MIN_LENGTH} characters`;
    }
    if (!confirmPassword) {
      next.confirmPassword = 'Please type your new password again';
    } else if (password !== confirmPassword) {
      next.confirmPassword = 'The two passwords do not match';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSaving || !validate()) {
      return;
    }
    setIsSaving(true);
    dispatch(
      updateUserPassword({
        userId,
        password,
        callback: (response) => {
          setIsSaving(false);
          if (!response) {
            return;
          }
          setPassword('');
          setConfirmPassword('');
          setErrors({});
          dispatch(setMustSetPassword({ mustSetPassword: false }));
        },
      })
    );
  };

  const handleSignOut = () => {
    dispatch(logOut());
  };

  return (
    <StyledContainer component="main">
      <StyledCard>
        <StyledCardContent component="form" onSubmit={handleSubmit}>
          <Typography variant="h4" component="h1" gutterBottom>
            Set your password
          </Typography>
          <StyledHelper variant="body2">
            {brandName} needs a password for your account. Your old system password doesn't carry over. Pick a new one to finish signing in.
          </StyledHelper>
          <StyledTextField
            required
            fullWidth
            autoFocus
            id="password"
            variant="outlined"
            name="password"
            label="New password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password || `At least ${MIN_LENGTH} characters`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockOutlined />
                </InputAdornment>
              ),
            }}
          />
          <StyledTextField
            required
            fullWidth
            id="confirmPassword"
            variant="outlined"
            name="confirmPassword"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockOutlined />
                </InputAdornment>
              ),
            }}
          />
          <StyledButton type="submit" fullWidth variant="contained" color="primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Set password'}
          </StyledButton>
          <StyledFooterRow>
            <Typography variant="body2" align="center">
              Not you? <StyledLink onClick={handleSignOut}>Sign out</StyledLink>
            </Typography>
          </StyledFooterRow>
        </StyledCardContent>
      </StyledCard>

      <StyledCopyright variant="body2" color="textSecondary" align="center">
        {'Copyright © '}
        <Link color="inherit" href="https://pollux-mui.com/">
          {brandName}
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </StyledCopyright>
    </StyledContainer>
  );
}

export default SetPassword;
